import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Trash2, RotateCcw, ArrowLeft, Search, Loader2, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const COLLECTION_LABELS = {
    prospects: 'Prospect',
    visites: 'Visite',
    inventaires: 'Inventaire',
    devis: 'Devis',
    clients: 'Client',
    aot: 'AOT',
    monte_meuble: 'Monte-meuble',
    garde_meuble: 'Garde-meuble',
    factures: 'Facture',
    problemes: 'Problème',
    taches: 'Tâche',
    taches_a_effectuer: 'Tâche à effectuer',
    pointages: 'Pointage',
};

const COLLECTION_COLORS = {
    prospects: 'bg-blue-100 text-blue-700 border-blue-200',
    visites: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    inventaires: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    devis: 'bg-violet-100 text-violet-700 border-violet-200',
    clients: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    aot: 'bg-amber-100 text-amber-700 border-amber-200',
    monte_meuble: 'bg-orange-100 text-orange-700 border-orange-200',
    garde_meuble: 'bg-purple-100 text-purple-700 border-purple-200',
    factures: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    problemes: 'bg-red-100 text-red-700 border-red-200',
    taches_a_effectuer: 'bg-pink-100 text-pink-700 border-pink-200',
};

function formatDate(dateStr) {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return '-'; }
}

function daysBetween(dateStr) {
    if (!dateStr) return 0;
    const d = new Date(dateStr);
    const now = new Date();
    return Math.max(0, Math.round((d - now) / (1000 * 60 * 60 * 24)));
}

export default function CorbeillePage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');

    const [confirmAction, setConfirmAction] = useState(null);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/corbeille`, { withCredentials: true });
            setItems(data.items || []);
        } catch {
            setItems([]);
            toast.error('Impossible de charger la corbeille');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, []);

    const handleRestore = async (id) => {
        try {
            await axios.post(`${API}/api/corbeille/${id}/restore`, {}, { withCredentials: true });
            toast.success('Élément restauré dans son module d\'origine');
            fetchItems();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la restauration');
        }
    };

    const handlePermanentDelete = async (id) => {
        try {
            await axios.delete(`${API}/api/corbeille/${id}`, { withCredentials: true });
            toast.success('Élément supprimé définitivement');
            fetchItems();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const filtered = items.filter(it => {
        if (filter !== 'all' && it.original_collection !== filter) return false;
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (
            (it.label || '').toLowerCase().includes(q)
            || (it.deleted_by_name || '').toLowerCase().includes(q)
            || (it.preview?.client_nom || '').toLowerCase().includes(q)
        );
    });

    const counts = items.reduce((acc, it) => {
        acc[it.original_collection] = (acc[it.original_collection] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="container mx-auto p-6 max-w-7xl" data-testid="corbeille-page">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="corbeille-back-btn">
                    <ArrowLeft size={16} className="mr-1" />
                    Retour
                </Button>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Trash2 size={24} className="text-red-500" />
                    Corbeille
                </h1>
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 ml-2">
                    {items.length} élément{items.length > 1 ? 's' : ''}
                </Badge>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6 flex items-start gap-2 text-sm">
                <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-amber-900">
                    Les éléments supprimés sont conservés <span className="font-semibold">30 jours</span> avant suppression définitive automatique.
                </p>
            </div>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Rechercher (nom, client, suppression par...)"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="corbeille-search-input"
                            />
                        </div>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-full sm:w-[260px]" data-testid="corbeille-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les types ({items.length})</SelectItem>
                                {Object.entries(counts).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>
                                        {COLLECTION_LABELS[k] || k} ({v})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-800">
                        Éléments en corbeille ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <Trash2 size={32} className="mx-auto mb-3 text-slate-300" />
                            <p className="text-sm">La corbeille est vide</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[820px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Élément</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Supprimé le</TableHead>
                                        <TableHead>Par</TableHead>
                                        <TableHead>Expire dans</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((it) => {
                                        const days = daysBetween(it.expires_at);
                                        return (
                                            <TableRow key={it.id} data-testid={`corbeille-row-${it.id}`}>
                                                <TableCell>
                                                    <p className="font-medium text-slate-900">{it.label || '—'}</p>
                                                    {it.preview?.telephone && (
                                                        <p className="text-xs text-slate-400">{it.preview.telephone}</p>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`${COLLECTION_COLORS[it.original_collection] || 'bg-slate-100 text-slate-700'} border`}>
                                                        {it.type_label || COLLECTION_LABELS[it.original_collection] || it.original_collection}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-slate-600 text-sm">
                                                    {formatDate(it.deleted_at)}
                                                </TableCell>
                                                <TableCell className="text-slate-600 text-sm">
                                                    {it.deleted_by_name || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`border ${days <= 3 ? 'bg-red-100 text-red-700 border-red-200' : days <= 7 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                                        <Clock size={11} className="mr-1" />
                                                        {days}j
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setConfirmAction({ type: 'restore', id: it.id, label: it.label })}
                                                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                                            title="Restaurer"
                                                            data-testid={`corbeille-restore-${it.id}`}
                                                        >
                                                            <RotateCcw size={14} className="mr-1" />
                                                            Restaurer
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => setConfirmAction({ type: 'delete', id: it.id, label: it.label })}
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                            title="Supprimer définitivement"
                                                            data-testid={`corbeille-delete-${it.id}`}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirmAction?.type === 'restore' ? 'Restaurer cet élément ?' : 'Supprimer définitivement ?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmAction?.type === 'restore'
                                ? <>L'élément <span className="font-semibold">{confirmAction?.label}</span> sera replacé dans son module d'origine.</>
                                : <>L'élément <span className="font-semibold">{confirmAction?.label}</span> sera supprimé <span className="text-red-600 font-semibold">définitivement</span> et ne pourra plus être restauré.</>}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="corbeille-confirm-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (confirmAction?.type === 'restore') handleRestore(confirmAction.id);
                                else if (confirmAction?.type === 'delete') handlePermanentDelete(confirmAction.id);
                                setConfirmAction(null);
                            }}
                            className={confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
                            data-testid="corbeille-confirm-ok"
                        >
                            {confirmAction?.type === 'restore' ? 'Restaurer' : 'Supprimer'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
